import { useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { useQuery, useQueryClient } from "@tanstack/react-query";

export const useAuth = () => {
  const [session, setSession] = useState<Session | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Use React Query for auth state with 5 minute cache
  const { data: user = null, isLoading: loading } = useQuery({
    queryKey: ['auth', 'user'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      return session?.user ?? null;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - auth data rarely changes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    // Set up auth state listener for real-time updates
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        // Immediately update both local state AND cache synchronously
        const userData = session?.user ?? null;
        queryClient.setQueryData(['auth', 'user'], userData);
      }
    );

    return () => subscription.unsubscribe();
  }, [queryClient]);

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      const redirectUrl = `${window.location.origin}/dashboard`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
          }
        }
      });

      if (error) throw error;

      toast.success("Account created successfully!");
      return { data, error: null };
    } catch (error: any) {
      logger.error("Sign up failed", error);
      toast.error("Unable to create account. Please try again.");
      return { data: null, error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      toast.success("Logged in successfully!");
      return { data, error: null };
    } catch (error: any) {
      logger.error("Sign in failed", error);
      toast.error("Unable to log in. Please check your credentials.");
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      toast.success("Logged out successfully");
      navigate("/");
    } catch (error: any) {
      logger.error("Sign out failed", error);
      toast.error("Unable to log out. Please try again.");
    }
  };

  return {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
  };
};
